import {Group} from 'src/design/atoms/Group';
import {Text} from 'src/design/atoms/Text';
import type {FC} from 'react';
import {RefreshButton} from 'src/design/molecules/RefreshButton';
import {useConnectivity} from 'src/offline/useConnectivity';
import {showAlert} from 'src/shared/hooks/useAlert';
import {formatRelativeOrNever} from 'src/shared/utils/formatters';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {ensureFormsOfflineSession} from './formsOfflineSession';
import {useFormsCacheStatus} from './useFormsCacheStatus';

const statusText = (
  cachedAt: Date | undefined,
  isRefreshing: boolean,
  isOffline: boolean
): string => {
  if (isRefreshing) return 'Offline copy: Updating…';
  if (isOffline) {
    return cachedAt
      ? `Offline copy: ${formatRelativeOrNever(cachedAt)} — reconnect to refresh`
      : 'Offline copy: Not saved yet — reconnect to save';
  }
  return cachedAt
    ? `Offline copy: ${formatRelativeOrNever(cachedAt)}`
    : 'Offline copy: Not saved yet';
};

// Freshness of the offline forms cache plus its manual refresh; the button is
// hidden (not disabled) offline because refreshing needs the network.
export const FormsOfflineRefreshAction: FC = () => {
  const {cachedAt, isRefreshing} = useFormsCacheStatus();
  const {isOffline} = useConnectivity();

  const handleRefresh = async (): Promise<void> => {
    try {
      await ensureFormsOfflineSession({refresh: true, force: true});
      showAlert('Offline forms updated on this device.', 'success');
    } catch (error) {
      showAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed" aria-live="polite">
        {statusText(cachedAt, isRefreshing, isOffline)}
      </Text>
      {!isOffline && (
        <RefreshButton
          size="sm"
          loading={isRefreshing}
          onClick={() => void handleRefresh()}>
          Refresh offline forms
        </RefreshButton>
      )}
    </Group>
  );
};
